import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ProjectFile } from '@/api/client';

export interface UseEmailAttachmentsReturn {
  emailAttachmentFileIds: Set<string>;
  emailAttachmentIdsSorted: string[];
  emailFilesPopoverOpen: boolean;
  setEmailFilesPopoverOpen: (open: boolean) => void;
  handleToggleEmailAttachment: (fileId: string, nextSelected: boolean) => void;
}

function fileIdsMatchingTypes(files: ProjectFile[], typeSet: Set<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const f of files) {
    const ft = String(f.file_type ?? '').trim();
    if (!ft || !typeSet.has(ft)) continue;
    const id = f.file_id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Хук управления прикреплёнными файлами для письма.
 * По умолчанию при открытии или смене проекта выбор пустой.
 * Если передан `preselectFileTypes`, при смене проекта выбираются все файлы с этими `file_type`,
 * а при подгрузке списка файлов новые совпадения добавляются к выбору (ручной выбор сохраняется).
 */
export function useEmailAttachments({
  open,
  projectId,
  projectFiles,
  preselectFileTypes,
}: {
  open: boolean;
  projectId: string;
  /** Fallback, пока не загружен список файлов с API */
  projectFiles: ProjectFile[];
  /** Значения `file_type` файлов проекта — автоматически включать в выбор при открытии и при появлении новых */
  preselectFileTypes?: readonly string[];
}): UseEmailAttachmentsReturn {
  const [emailAttachmentFileIds, setEmailAttachmentFileIds] = useState<Set<string>>(
    () => new Set()
  );
  const [emailFilesPopoverOpen, setEmailFilesPopoverOpen] = useState(false);
  const lastProjectIdRef = useRef<string | null>(null);

  const { data: fetchedFiles } = useQuery({
    queryKey: ['projectFiles', projectId],
    queryFn: () => api.projectFiles.list(projectId),
    enabled: open && Boolean(projectId),
    staleTime: 60_000,
  });

  const projectFilesResolved = fetchedFiles ?? projectFiles;

  useEffect(() => {
    if (!open) {
      setEmailFilesPopoverOpen(false);
      lastProjectIdRef.current = null;
      return;
    }

    const validIds = new Set(projectFilesResolved.map((f) => f.file_id));
    const typeSet =
      preselectFileTypes && preselectFileTypes.length > 0
        ? new Set(preselectFileTypes.map((t) => t.trim()).filter(Boolean))
        : null;
    const autoIds = typeSet ? fileIdsMatchingTypes(projectFilesResolved, typeSet) : [];
    const autoSet = new Set(autoIds);

    const projectChanged = lastProjectIdRef.current !== projectId;
    lastProjectIdRef.current = projectId;

    setEmailAttachmentFileIds((prev) => {
      if (!typeSet) {
        if (projectChanged) {
          return new Set();
        }
        const next = new Set<string>();
        for (const id of prev) {
          if (validIds.has(id)) next.add(id);
        }
        if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
        return next;
      }

      if (projectChanged) {
        return autoSet;
      }

      const next = new Set(prev);
      let changed = false;
      for (const id of autoSet) {
        if (validIds.has(id) && !next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      for (const id of [...next]) {
        if (!validIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      if (!changed && next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [open, projectId, projectFilesResolved, preselectFileTypes]);

  const emailAttachmentIdsSorted = useMemo(() => {
    const knownIds = new Set(projectFilesResolved.map((f) => f.file_id));
    return [...emailAttachmentFileIds].filter((id) => knownIds.has(id)).sort();
  }, [emailAttachmentFileIds, projectFilesResolved]);

  const handleToggleEmailAttachment = useCallback((fileId: string, nextSelected: boolean) => {
    setEmailAttachmentFileIds((prev) => {
      const n = new Set(prev);
      if (nextSelected) n.add(fileId);
      else n.delete(fileId);
      return n;
    });
  }, []);

  return {
    emailAttachmentFileIds,
    emailAttachmentIdsSorted,
    emailFilesPopoverOpen,
    setEmailFilesPopoverOpen,
    handleToggleEmailAttachment,
  };
}
